import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import type { ReactNode } from 'react';
import { ApiError } from '../lib/api';
import type { LoadState } from '../lib/types';

/* --------------------------------------------------------------------------
   The client cache and the Refresh contract.

   Refresh is a real escape hatch, not `location.reload()`: it cancels every
   request in flight, drops cached data, and re-fetches — so a wedged screen
   recovers without the app being torn down and rebuilt.
   ----------------------------------------------------------------------- */

const cache = new Map<string, unknown>();

interface DataCtx {
  /** Bumped by refresh; every query re-runs when it changes. */
  generation: number;
  refreshing: boolean;
  lastRefreshAt: number | null;
  /** Cancel in flight, purge cache, refetch. */
  refresh: () => void;
  /** Also clears local storage, caches and the service worker registration. */
  hardReset: () => Promise<void>;
  registerAbort: (c: AbortController) => () => void;
}

const Ctx = createContext<DataCtx | null>(null);

export function DataProvider({ children }: { children: ReactNode }) {
  const [generation, setGeneration] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshAt, setLastRefreshAt] = useState<number | null>(null);
  const live = useRef(new Set<AbortController>());
  const inflight = useRef(0);

  const registerAbort = useCallback((c: AbortController) => {
    live.current.add(c);
    inflight.current += 1;
    return () => {
      live.current.delete(c);
      inflight.current = Math.max(0, inflight.current - 1);
      if (inflight.current === 0) setRefreshing(false);
    };
  }, []);

  const refresh = useCallback(() => {
    for (const c of live.current) c.abort();
    live.current.clear();
    cache.clear();
    setRefreshing(true);
    setLastRefreshAt(Date.now());
    setGeneration((g) => g + 1);
  }, []);

  const hardReset = useCallback(async () => {
    refresh();
    try { localStorage.clear(); sessionStorage.clear(); } catch { /* blocked storage */ }
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch { /* nothing registered yet, or the browser refused — not fatal */ }
  }, [refresh]);

  // A refresh with nothing on screen should not spin forever.
  useEffect(() => {
    if (!refreshing) return;
    const t = setTimeout(() => setRefreshing(false), 6000);
    return () => clearTimeout(t);
  }, [refreshing, generation]);

  const value = useMemo<DataCtx>(
    () => ({ generation, refreshing, lastRefreshAt, refresh, hardReset, registerAbort }),
    [generation, refreshing, lastRefreshAt, refresh, hardReset, registerAbort],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useData(): DataCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useData must be used inside <DataProvider>');
  return c;
}

/** Just the refresh controls, for the header. */
export function useRefresh(): Pick<DataCtx, 'refresh' | 'hardReset' | 'refreshing' | 'lastRefreshAt'> {
  const { refresh, hardReset, refreshing, lastRefreshAt } = useData();
  return { refresh, hardReset, refreshing, lastRefreshAt };
}

export interface QueryResult<T> {
  data: T | null;
  error: ApiError | null;
  state: LoadState;
  /** True the first time only — use it to choose skeleton vs stale-while-refresh. */
  loading: boolean;
  /** Re-run this query alone. */
  reload: () => void;
}

/**
 * Fetch once per key, cancel on unmount, re-run on Refresh.
 *
 * The `key` must encode every parameter — it is the cache identity:
 *
 *     const { data, error, loading } = useQuery(
 *       `summary:${venue}:${range.from}:${range.to}`,
 *       (o) => api.summary({ from: range.from, to: range.to, venue }, o),
 *     );
 *
 * `fetcher` is read from a ref, so it does not need to be memoised.
 */
export function useQuery<T>(
  key: string,
  fetcher: (opts: { signal: AbortSignal }) => Promise<T>,
): QueryResult<T> {
  const { generation, registerAbort } = useData();
  // A local nonce, in the effect's dependencies, is what makes `reload()`
  // actually re-run this one query — clearing the cache alone would not.
  const [nonce, setNonce] = useState(0);
  const [data, setData] = useState<T | null>(() => (cache.get(key) as T | undefined) ?? null);
  const [error, setError] = useState<ApiError | null>(null);
  const [state, setState] = useState<LoadState>(() => (cache.has(key) ? 'ready' : 'idle'));
  const fn = useRef(fetcher);
  fn.current = fetcher;

  useEffect(() => {
    const cached = cache.get(key) as T | undefined;
    if (cached !== undefined) { setData(cached); setState('ready'); }
    else setData(null);

    const ctrl = new AbortController();
    const unregister = registerAbort(ctrl);
    let alive = true;

    setState(cached !== undefined ? 'refreshing' : 'loading');
    setError(null);

    fn.current({ signal: ctrl.signal })
      .then((d) => {
        cache.set(key, d);
        if (!alive) return;
        setData(d);
        setState('ready');
      })
      .catch((e: unknown) => {
        const err = ApiError.from(e);
        // A cancelled request is not a failure — it is the Refresh working.
        if (err.kind === 'aborted' || !alive) return;
        setError(err);
        setState('error');
      })
      .finally(() => { unregister(); });

    return () => { alive = false; ctrl.abort(); };
  }, [key, generation, nonce, registerAbort]);

  const reload = useCallback(() => {
    cache.delete(key);
    setNonce((n) => n + 1);
  }, [key]);

  return {
    data, error, state,
    loading: state === 'loading' || state === 'idle',
    reload,
  };
}
